﻿/// <reference path="../../typings/jquery.popupoverlay.d.ts" />

namespace eXpress.core {
	export interface IPopupOptions {
		readonly canUseJQueryPopup?: boolean;
	}

	export interface IOkCancelAndClosePopupOptions {
		readonly dialogId?: string;
		readonly cssClass?: string;
		readonly okFunction?: () => void;
	}

	export interface IContentDialogOptions extends IOkCancelAndClosePopupOptions {
		readonly labelContentKey: string;
		readonly okContentKey?: string;
		readonly cancelContentKey?: string;
		readonly titleContentKey?: string;
		readonly replacements?: string[];
		readonly titleReplacements?: string[];
		readonly forceRefresh?: boolean;
	}

	export interface IViewDialogOptions {
		readonly dialogId: string;
		readonly cssClass?: string;
		readonly view: string;
		readonly forceRefresh?: boolean;
		readonly fromCache?: boolean;
	}

	interface IClosablePopupOptions extends IPopupOptions {
		readonly $closeButtons?: JQuery;
		readonly hideCloseIcon?: boolean;
		readonly closeOnClickOutsidePopup?: boolean;
		readonly closeEvent?: () => void;
	}

	export interface IOpenedPopup<TResult> {
		close(value: TResult): void;
		readonly isClosed: boolean;
		readonly promise: JQueryPromise<TResult>;
	}

	enum ProductOrientation {
		Square,
		Landscape,
		Portrait
	}

	// Core function to open/close a popup
	export function openPopupAsync<TCloseResult>(popupEl: HTMLElement, options: { onVisible: () => void, canUseJQueryPopup?: boolean }): IOpenedPopup<TCloseResult> {
		const deferred = $.Deferred<TCloseResult>();
		const $popup = $(popupEl);

		if (popupEl.id !== "leave-intent-dialog" && typeof window.DisableLeaveIntent !== "undefined") {
			window.DisableLeaveIntent();
		}

		const $cover = $("#maindialogcover");
		const $html = $("html");
		const $body = $("body");
		const currentY = window.pageYOffset;

		$popup.show();
		$cover.show();

		document.documentElement.style.marginTop = "-" + Math.round(currentY) + "px";

		$html.addClass("has-open-dialog u-no-scroll");
		$body.addClass("u-no-scroll");

		if (options.canUseJQueryPopup) {
			$popup.popup({
				background: false,
				escape: false,
				blur: false,
				scrolllock: false
			});

			$popup.popup('show');
		}
		else {
			$popup.css('display', 'flex');
		}

		const result = {
			close: function (value: TCloseResult) {
				if (options.canUseJQueryPopup) {
					$popup.popup("hide");
				}
				else {
					$popup.hide();
				}
				$cover.hide();
				$html.removeClass("has-open-dialog u-no-scroll");
				$body.removeClass("u-no-scroll");

				document.documentElement.style.marginTop = "0px";

				window.scroll(0, currentY);
				if (popupEl.id !== "leave-intent-dialog" && typeof window.EnableLeaveIntent !== "undefined") {
					window.EnableLeaveIntent();
				}
				this.isClosed = true;
				deferred.resolve(value);
			},
			promise: deferred.promise(),
			isClosed: false
		};

		if (options && options.onVisible)
			window.setTimeout(() => {
				if (!result.isClosed)
					options.onVisible();
			}, 200);

		return result;
	}

	/**
	 * Popup with minimum class.
	 * Using this class should prevent opening/closing twice and
	 * does some basic logging
	 */
	export class Popup<TResult> {

		protected popupEl: HTMLElement;
		private openedPopup: IOpenedPopup<TResult> | null;
		public canUseJQueryPopup: boolean;

		constructor(popupEl: HTMLElement, { canUseJQueryPopup = true }: IPopupOptions = {}) {
			this.popupEl = popupEl;
			this.canUseJQueryPopup = canUseJQueryPopup;
		}

		public isOpen(): boolean {
			return !!this.openedPopup;
		}

		/** Promise resolves when dialog closes */
		public openAsync(): JQueryPromise<TResult> {
			this.openedPopup = openPopupAsync(this.popupEl, { onVisible: () => this.onVisible(), canUseJQueryPopup: this.canUseJQueryPopup }); // didn't pass options, else it will bubble through to all derived classes

			return this.openedPopup.promise;
		}

		/** Close Dialog, specify value to resolve dialog promise with (didn't make it optional) */
		public close(value: TResult): boolean {
			if (this.isOpen() && this.openedPopup) {
				this.openedPopup.close(value);
				this.openedPopup = null;
				return true;
			}
			return false;
		}

		public makeInvisible(): void {
			this.popupEl.style.opacity = '0';
		}

		protected onVisible(): void {
			return;
		}
	}

	/*
	 * Used for popups that can be closed by user
	 */
	export class ClosablePopup<TResult> extends Popup<TResult | null> {

		protected $popup: JQuery;
		private $closeButtons: JQuery | undefined;
		protected namespace: string;
		protected hideCloseIcon: boolean;
		private closeOnClickOutsidePopup: boolean;
		private static popupCounter: number;
		private closeEvent?: () => void;

		constructor($popup: JQuery, options: IClosablePopupOptions) {
			super($popup[0], options);

			const {
				$closeButtons,
				hideCloseIcon = false,
				closeOnClickOutsidePopup = true,
				closeEvent
			} = options;

			this.$popup = $popup;
			this.$closeButtons = $closeButtons;
			this.hideCloseIcon = hideCloseIcon;
			this.closeOnClickOutsidePopup = closeOnClickOutsidePopup;
			ClosablePopup.popupCounter = (ClosablePopup.popupCounter || 0) + 1;
			this.namespace = ".popup" + ClosablePopup.popupCounter;
			this.closeEvent = closeEvent;
		}

		public openAsync(): JQueryPromise<TResult | null> {
			if (this.hideCloseIcon)
				$(this.popupEl).find(".dialog-header .button.close").hide();

			this.init();
			return super.openAsync().always(() => { this.deinit(); });
		}

		public close(value: TResult | null) {
			if (this.closeEvent) this.closeEvent();
			return super.close(value);
		}

		protected init(): void {
			if (this.$closeButtons) {
				this.$closeButtons
					.on("click" + this.namespace, e => {
						this.close(null);
						return false;
					});
			}

			// close popup when click outside the popup
			if (this.closeOnClickOutsidePopup) {
				$(this.popupEl).parents().on("mousedown" + this.namespace, e => {
					const $target = $(e.target);
					if (e.target.id && $target.parents('#' + this.popupEl.id).length < 1) {
						this.close(null);
					}
				});
			}

			// close popup when enter is pressed
			$(document).on("keydown" + this.namespace, function (e) {
				if (e.which === 13 || e.keyCode === 13) { // enter is 13, some browsers do not support 'keycode' others 'which'
					$('.active.main').click();
				}
			});
		}

		protected deinit(): void {
			if (this.$closeButtons) {
				this.$closeButtons.off("click" + this.namespace);
			}

			// close popup when click outside the popup
			$(this.popupEl).parents().off("mousedown" + this.namespace);

			// close popup when enter is pressed
			$(document).off("keydown" + this.namespace);
		}
	}

	export class OkCancelAndClosePopup extends eXpress.core.ClosablePopup<boolean> {
		constructor($popup: JQuery, options: IClosablePopupOptions = { hideCloseIcon: false }) {
			super($popup, {
				$closeButtons: $popup.find(".dialog-header .button.close"),
				...options
			});
		}

		protected init(): void {
			super.init();

			this.$popup.find(".ok").on("click" + this.namespace, (e) => this.close(true));
			this.$popup.find(".cancel").on("click" + this.namespace, (e) => this.close(false));
		}

		protected deinit(): void {
			super.deinit();

			this.$popup.find(".ok").off(this.namespace);
			this.$popup.find(".cancel").off(this.namespace);
		}
	}

	interface ICreateContentDialogOptions extends IContentDialogOptions {
		readonly templateSelector: string;
		readonly bodySelector: string;
		readonly titleSelector: string;
		readonly okSelector: string;
		readonly okTextSelector: string;
		readonly cancelSelector: string;
		readonly cancelTextSelector: string;
		readonly forceRefresh?: boolean;
	}

	const createContentDialogAsync = (contentManager, options: ICreateContentDialogOptions) => {
		let $popup: JQuery;

		const { dialogId,
			templateSelector,
			cssClass, labelContentKey,
			cancelContentKey,
			okContentKey,
			titleContentKey,
			replacements,
			titleReplacements,
			bodySelector,
			okSelector,
			okTextSelector,
			cancelSelector,
			cancelTextSelector,
			forceRefresh
		} = options;

		if (dialogId) {
			$popup = $("#" + dialogId);
			if (forceRefresh && $popup.length) {
				$popup.remove();
			}
			else if ($popup.length) {
				return $.Deferred<JQuery>().resolve($popup).promise();
			}
		}

		// start with a clone of the dialog template
		$popup = $(templateSelector).clone();
		if (dialogId)
			$popup.attr("id", dialogId);

		if (cssClass) {
			$popup.addClass(cssClass);
		}

		return contentManager.getLabelsAsync([labelContentKey, cancelContentKey, okContentKey, titleContentKey])
			.then(() => {
				let titleContent = options.titleContentKey ? contentManager.getLabel(options.titleContentKey) : "";
				if (titleReplacements) {
					titleContent = utils.formatString(titleContent, ...titleReplacements);
				}

				$popup.find(options.titleSelector).text(titleContent);

				if (options.cssClass)
					$popup.addClass(options.cssClass);

				let bodyContent = contentManager.getLabel(options.labelContentKey);

				if (replacements)
					bodyContent = utils.formatString(bodyContent, ...replacements);

				$popup.find(bodySelector).html(bodyContent);

				let okContent = "";
				if (okContentKey)
					okContent = contentManager.getLabel(okContentKey);
				$popup.find(okSelector)
					.toggle(!!okContent)
					.find(okTextSelector).html(okContent);

				let cancelContent = "";
				if (cancelContentKey)
					cancelContent = contentManager.getLabel(cancelContentKey);
				$popup.find(cancelSelector)
					.toggle(!!cancelContent)
					.find(cancelTextSelector).html(cancelContent);

				// add popup to dom
				if (dialogId)
					$(".renderedDialogs").append($popup);

				return $popup;
			});
	};

	export class ContentPopup extends eXpress.core.OkCancelAndClosePopup {

		constructor($popup: JQuery, hideCloseIcon = false) {
			super($popup, { hideCloseIcon });
		}

		public static createDialogAsync(contentManager: ContentManager, options: IContentDialogOptions): JQueryPromise<JQuery> {
			return createContentDialogAsync(contentManager, {
				...options,
				templateSelector: '#dialogTemplate div.dialog-content',
				bodySelector: '.dialog-body',
				titleSelector: '.dialog-header .title',
				okSelector: '.dialog-footer .dialogbuttons .ok',
				okTextSelector: 'span.text',
				cancelSelector: '.dialog-footer .dialogbuttons .cancel',
				cancelTextSelector: 'span.text'
			});
		}
	}

	export class SelectAlbumPopup extends eXpress.core.ClosablePopup<string> {
		protected $popup: JQuery;

		constructor($popup: JQuery) {
			super($popup, {
				$closeButtons: $popup.find(".dialog-header .button.close")
			});
		}

		protected init(): void {
			super.init();
			this.$popup.find(".item").on("click" + this.namespace, (e) => {
				this.close($(e.currentTarget).attr("data-folder-id")!); // data() returns type ANY so could be a number instead of string!!!
			});
		}

		protected deinit(): void {
			super.deinit();
			this.$popup.find(".item").off(this.namespace);
		}

		protected onVisible() {
			if (this.canUseJQueryPopup) {
				$('.popup_wrapper_visible').on("click" + this.namespace, e => {
					if (!$(e.target).closest(`#${this.popupEl.id}`).length) {
						this.close(null);
					}
				});
			}
		}

		public static createDialogAsync(contentManager: ContentManager, titleContentKey: string, folders: core.IPhotoFolder[]): JQueryPromise<JQuery> {
			// start with a clone of the dialog template
			const $popup: JQuery = $("#dialogTemplate div.dialog-content").clone();

			// add default css class
			$popup.addClass("photo-upload-container");

			return contentManager.getLabelsAsync(["Label.MyPages.Main.MyAlbums.Album.DefaultTitle", "Label.MyPhotos.NewAlbum", "Label.MyPages.MyAlbums.NumberOfPhotos", titleContentKey])
				.then(() => {
					$popup.find(".dialog-header .title").html(contentManager.getLabel(titleContentKey));
					$popup.find(".dialog-header").addClass("photo-upload-header");

					const $dialogBody = $popup.find(".dialog-body");
					$dialogBody.addClass("photo-upload-content");

					const $newAlbum = $(
						`<div class="item new" data-folder-id="${core.emptyGuid}">
								<div class="thumb"><span class="icon-new-album" data-icon="&#xe916;" aria-hidden="true"></span></div>
								<div class="title"></div>
						</div>`
					);

					$newAlbum.find(".title").text(contentManager.getLabel("Label.MyPhotos.NewAlbum"));

					$dialogBody.append($newAlbum);

					const $folders = folders.map(folder => {
						let nrOfPhotos = contentManager.getLabel("Label.MyPages.MyAlbums.NumberOfPhotos");
						nrOfPhotos = utils.formatString(nrOfPhotos, folder.fileCount.toString());

						const $folder = $(
							`<div class="item folder" data-folder-id="${folder.id}">
									<div class="thumb ${!folder.thumbUrl ? `empty` : ""}">
										${folder.thumbUrl ? `<img src="${folder.thumbUrl}">` : ""}
									</div>
									<div class="title">
										<span class="name"></span>
										<span class="meta">${folder.dateCreated ? (folder.dateCreated.toLocaleDateString() + " - ") : ""}${nrOfPhotos}</span>
									</div>
							</div>`
						);

						$folder.find(".title span.name").text(folder.name);
						return $folder;
					});

					$dialogBody.append($folders);

					return $popup;
				});
		}
	}

	export class StyleguideContentPopup extends eXpress.core.ClosablePopup<boolean> {

		constructor($popup: JQuery, hideCloseIcon = false) {
			super($popup, {
				$closeButtons: $popup.find(".technical-popup-close"),
				hideCloseIcon: hideCloseIcon,
				canUseJQueryPopup: false
			});
		}

		public static createDialogAsync(contentManager: ContentManager, options: IContentDialogOptions): JQueryPromise<JQuery> {
			return createContentDialogAsync(contentManager, {
				...options,
				templateSelector: '#technical-popup-template',
				bodySelector: '.technical-popup-content',
				titleSelector: '.technical-popup-title',
				okSelector: '.technical-popup-okbutton',
				okTextSelector: 'span',
				cancelSelector: '.technical-popup-cancelbutton',
				cancelTextSelector: 'span'
			});
		}
	}

	export interface IStyleguidViewPopupOptions {
		readonly closeOnClickOutsidePopup: boolean;
		readonly closeEvent: () => void;
	}

	export class StyleguideViewPopup extends eXpress.core.ClosablePopup<boolean> {
		constructor($popup: JQuery, options: Partial<IStyleguidViewPopupOptions> = {}) {

			const {
				closeOnClickOutsidePopup,
				closeEvent
			} = options;

			super($popup, {
				$closeButtons: $popup.find(".technical-popup-close"),
				canUseJQueryPopup: false,
				closeEvent,
				closeOnClickOutsidePopup
			});
		}

		public static createDialogAsync(options: IViewDialogOptions, data?: any): JQueryPromise<JQuery> {
			let $popup: JQuery;
			if (options.dialogId) {
				$popup = $("#" + options.dialogId);
				if ($popup.length !== 0) {
					if (options.forceRefresh) {
						$popup.remove();
					} else {
						return $.Deferred<JQuery>().resolve($popup).promise();
					}
				}
			}

			// Get the html from the server
			return eXpress.services.ServiceBase.getHTML('/ajaxxhr/Dialog/' + options.view, data, options.fromCache !== false).then(
				res => {
					const $res = $(res);
					$(".renderedDialogs").append($res);

					if (options.dialogId)
						$res.attr("id", options.dialogId);

					if (options.cssClass)
						$res.addClass(options.cssClass);

					return $res;
				});
		}

		public static createCoreDialogAsync(options: IViewDialogOptions, data?: any): any {
			let $popup: JQuery;
			if (options.dialogId) {
				$popup = $("#" + options.dialogId);
				if ($popup.length !== 0) {
					if (options.forceRefresh) {
						$popup.remove();
					} else {
						return $.Deferred<JQuery>().resolve($popup).promise();
					}
				}
			}

			return $.ajax({
				url: '/apicore/dialog/' + options.view,
				type: 'POST',
				contentType: 'application/json',
				data: JSON.stringify(data)
			}).then(res => {
				const $res = $(res);
				$(".renderedDialogs").append($res);

				if (options.dialogId)
					$res.attr("id", options.dialogId);

				if (options.cssClass)
					$res.addClass(options.cssClass);

				return $res;
			});
		}
	}


	export class StyleguideViewDeliveryTimesPopup extends StyleguideViewPopup {
		private contentManager: ContentManager;

		constructor($popup: JQuery, contentManager: ContentManager, result: IDeliveryMethodInfosResult) {
			super($popup);

			this.contentManager = contentManager;
			this.renderPopup($popup, result);
		}

		private addDeliveryTime(item: core.IDeliveryPriceInfo, $source: JQuery, $destination: JQuery, mobile = false) {
			const $newRow = $source.clone();

			if (mobile) {
				const deliveryCategoryHtml = $newRow.find('.technical-delivery-times-mobile-body-category').html();
				if (deliveryCategoryHtml) $newRow.find('.technical-delivery-times-mobile-body-category').html(deliveryCategoryHtml.replace("[DELIVERYCATEGORY]", this.getDeliveryCategory(item.DeliveryCategory)));
			} else
				$newRow.find('.technical-delivery-times-header-category').html(this.getDeliveryCategory(item.DeliveryCategory));

			$newRow.find(".technical-delivery-times-body-method").html(item.DeliveryInformation);
			$newRow.find(".technical-delivery-times-body-date").html(item.DeliveryDate);
			$newRow.find(".technical-delivery-times-body-price").html(item.DeliveryPrice);

			$destination.append($newRow);
		}

		private getDeliveryCategory(categoryLabel: string): string {
			return this.contentManager.getLabel(`Label.ProductCategory.DeliveryInformation.Dialog.${categoryLabel}`, categoryLabel);
		}

		private getDeliveryMethodDescription(deliveryInformationLabel: string, trackAndTrace: boolean): string {
			const withTracking = utils.formatString(" {0}", this.contentManager.getLabel("Label.Delivery.DeliveryMethod.Home.WithTracking"));
			const withOutTracking = utils.formatString(" {0}", this.contentManager.getLabel("Label.Delivery.DeliveryMethod.Home.WithoutTracking"));
			return utils.formatString(deliveryInformationLabel, (trackAndTrace ? withTracking : withOutTracking));
		}

		private renderPopup($popup: JQuery, result: IDeliveryMethodInfosResult): void {
			$popup.find('.technical-delivery-times-title').html(result.Title);
			const $deliveryTimes = $popup.find(".technical-delivery-times");
			const $deliveryTimesMobile = $(".technical-delivery-times-mobile");
			const $emptyHeader = $popup.find('.technical-delivery-times-header').first().clone();
			const $emptyBody = $popup.find('.technical-delivery-times-body').first().clone();
			const $emptyRowMobile = $popup.find('.technical-delivery-times-row-mobile').first().clone();

			$deliveryTimes.empty();
			$deliveryTimesMobile.empty();

			this.contentManager.getLabelsAsync (
				[
					"Label.ProductCategory.DeliveryInformation.Dialog.PickUpDelivery",
					"Label.ProductCategory.DeliveryInformation.Dialog.HomeDelivery",
					"Label.Delivery.DeliveryMethod.Home.WithTracking",
					"Label.Delivery.DeliveryMethod.Home.WithoutTracking",
				]
			)
			.always(() => {
				let oldDeliveryCategory = "";
				const sortedArrayOfDeliveryMethodInfos = this.sortDeliveryMethodInfosArray(result.DeliveryMethodInfos, "DeliveryPrice");
				sortedArrayOfDeliveryMethodInfos.forEach(item => {
					if (oldDeliveryCategory !== item.DeliveryCategory)
						this.addDeliveryTime(item, $emptyHeader, $deliveryTimes);

					this.addDeliveryTime(item, $emptyBody, $deliveryTimes);
					this.addDeliveryTime(item, $emptyRowMobile, $deliveryTimesMobile, true);
					oldDeliveryCategory = item.DeliveryCategory;
				});
			});
		}

		public static createDialogAsync(options: IViewDialogOptions): JQueryPromise<JQuery> {
			return super.createDialogAsync(options);
		}

		private sortDeliveryMethodInfosArray(array, key: string) {
			return array.sort(function (a, b) {
				const pricea = a[key];
				const priceb = b[key];

				// converten naar double
				const convertedPricea = parseFloat(pricea);
				const convertedPriceb = parseFloat(priceb);

				// if not converted don't sort values
				if (!convertedPricea || !convertedPriceb)
					return 0;

				// compare category
				if (a.DeliveryCategory !== b.DeliveryCategory)
					return 0;

				// compaire keys
				if (convertedPricea > convertedPriceb) return 1;
				if (convertedPricea < convertedPriceb) return -1;
				return 0;
			});
		}
	}

	export class StyleguideViewOptionPricePopup extends StyleguideViewPopup {

		private currentPvc?: string;

		constructor($popup: JQuery, closeOnClickOutsidePopup = true) {
			super($popup, { closeOnClickOutsidePopup });
		}

		private pvcSelectChange(select: Element, quantityBreakListContainer: Element, quantityBreakListItems: Element[], selectedOption: string, noOptionsDiv: Element): void {

			let visibleItems = 0;

			if (!selectedOption)
				selectedOption = $(select).children("option").first().val() as string;
			else
				$(select).val(selectedOption);

			$(quantityBreakListItems).each(function (_, item) {
				if ($(item).data("pvc") === selectedOption) {
					$(this).show();
					visibleItems++;
				} else {
					$(this).hide();
				}
			});

			if (visibleItems > 0) {
				$(noOptionsDiv).hide();
				$(quantityBreakListContainer).show();
			} else {
				$(noOptionsDiv).show();
				$(quantityBreakListContainer).hide();
			}
		}

		protected init(): void {
			super.init();

			const pvcSelect = html.querySelector(this.$popup.get(0), ".technical-price-option-select", false);
			const quantityBreakListItemContainer = html.querySelector(this.$popup.get(0), ".p-option-price-details__body-container", true);
			const quantityBreakListItems = html.querySelectorAll(quantityBreakListItemContainer, ".m-quantity-break-list__item-container");
			const noOptionsDiv = html.querySelector(this.$popup.get(0), "#noOptionsDiv", true);

			if (quantityBreakListItemContainer && quantityBreakListItems && noOptionsDiv) {
				$(noOptionsDiv).hide();

				if (!pvcSelect) return;

				if (this.currentPvc)
					this.pvcSelectChange(pvcSelect, quantityBreakListItemContainer, quantityBreakListItems, this.currentPvc, noOptionsDiv);
				else
					this.pvcSelectChange(pvcSelect, quantityBreakListItemContainer, quantityBreakListItems, $(pvcSelect).children('option:first-child').val() as string, noOptionsDiv);

				$(pvcSelect).on("change blur" + this.namespace, (e) => {
					this.pvcSelectChange(e.target, quantityBreakListItemContainer, quantityBreakListItems, $(e.target).children('option:selected').val() as string, noOptionsDiv);
				});
			}
		}

		protected deinit(): void {
			super.deinit();

			this.$popup.find(".m-select-box").off(this.namespace);
		}

		public openAsync(currentPvc?: string): JQueryPromise<boolean | null> {
			this.currentPvc = currentPvc;
			this.init();
			return super.openAsync();
		}

		private getSizeFromPvc(currentPvc: string): string {
			let orientation = ProductOrientation.Square;
			let ratioWidth = 0;
			let ratioHeight = 0;
			let width = 0;
			let height = 0;
			let depth = 0;
			let calcFromRatio = false;
			const ratioParts = currentPvc.split('~');

			if (ratioParts.length > 1) {
				const ratioDimensions = ratioParts[1].split('x');
				ratioWidth = Number(ratioDimensions[0]);
				ratioHeight = Number(ratioDimensions[1]);
				if (ratioWidth > ratioHeight) {
					orientation = ProductOrientation.Landscape;
				} else {
					orientation = ProductOrientation.Portrait;
				}
				calcFromRatio = true;
			}

			const sizeParts = ratioParts[0].split('x');

			switch (sizeParts.length) {
				case 1:
					if (orientation === ProductOrientation.Landscape) {
						height = Number(sizeParts[0].replace(/[^0-9]/g, ''));
						width = height * ratioWidth / ratioHeight;
					} else {
						width = Number(sizeParts[0].replace(/[^0-9]/g, ''));
						height = width * ratioHeight / ratioWidth;
					}
					break;
				case 2:
					width = Number(sizeParts[0].replace(/[^0-9]/g, ''));
					height = Number(sizeParts[1].replace(/[^0-9]/g, ''));
					break;
				default:
					depth = Number(sizeParts[2].replace(/[^0-9]/g, ''));
					if (calcFromRatio) {
						return width + " x " + height + " x " + depth + " cm";
					} else {
						if (orientation === ProductOrientation.Portrait) {
							return width + " x " + height + " x " + depth + " cm";
						} else {
							return height + " x " + width + " x " + depth + " cm";
						}
					}
			}

			if (calcFromRatio) {
				return width + " x " + height + " cm";
			} else {
				if (orientation === ProductOrientation.Portrait) {
					return width + " x " + height + " cm";
				} else {
					return height + " x " + width + " cm";
				}
			}
		}
	}
}
